import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import TourPopup from '../components/TourPopup';
import ChinaConsultPopup from '../components/ChinaConsultPopup';
import { supabase } from '../lib/supabase';

const Container = styled.section`
  background-color: #efefef;
  padding: 40px 0 135px;
  position: relative;
  z-index: 2;
  clear: both;
  
  @media (max-width: 479px) {
    padding: 20px 0 60px;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
`;


const ToursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TourCard = styled.div`
  background-color: transparent;
  border-radius: 34px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const CardWrapper = styled.div`
  background-color: transparent;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ImageWrapper = styled.div`
  width: 100%;
  padding-bottom: 66%;
  position: relative;
  overflow: hidden;
  border-radius: 34px;
  cursor: pointer;
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const Mark = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  background-color: #fcaf66;
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const CardContent = styled.div`
  padding: 20px 0;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  font-family: 'Geologica', Arial, sans-serif;
  margin-bottom: 10px;
  cursor: pointer;
  
  strong {
    font-weight: 700;
  }
  
  &:hover {
    color: #ff9605;
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  font-family: 'Geologica', Arial, sans-serif;
  color: #666;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
`;

const PrimaryButton = styled(Button)`
  color: #ffffff;
  background: linear-gradient(0.75turn, #FF9966, #FF5E62);
  box-shadow: 0px 5px 15px 0px rgba(255, 153, 102, 0.5);
  
  &:hover {
    opacity: 0.9;
  }
`;

const SecondaryButton = styled(Button)`
  color: #000000;
  background-color: transparent;
  border: 1px solid #000000;
  
  &:hover {
    background-color: #000000;
    color: #ffffff;
  }
`;

const ChinaToursPage = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTour, setSelectedTour] = useState(null);
  const [isTourPopupOpen, setIsTourPopupOpen] = useState(false);
  const [isConsultPopupOpen, setIsConsultPopupOpen] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchTours();
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchTours = async () => {
    try {
      // Add timeout to prevent infinite loading
      const fetchPromise = supabase
        .from('tours')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Fetch timeout')), 10000)
      );
      
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);

      if (error) throw error;
      if (isMounted.current) {
        setTours(data || []);
      }
    } catch (error) {
      console.error('Error fetching tours:', error);
      // Set empty array on error
      if (isMounted.current) {
        setTours([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleTourClick = (tour) => {
    setSelectedTour(tour);
    setIsTourPopupOpen(true);
  };

  const handleBookClick = (tour) => {
    setSelectedTour(tour);
    setIsConsultPopupOpen(true);
  };

  // Новая функция для обработки нажатия на кнопку "Оставить заявку" в попапе тура
  const handleTourPopupBookClick = (tour) => {
    setSelectedTour(tour);
    setIsConsultPopupOpen(true);
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <div style={{ textAlign: 'center', padding: '50px' }}>Загрузка туров...</div>
        </Content>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Content>
          <ToursGrid>
            {tours.map((tour) => (
              <TourCard key={tour.id}>
                <CardWrapper>
                  <ImageWrapper onClick={() => handleTourClick(tour)}>
                    {tour.mark && <Mark>{tour.mark}</Mark>}
                    <img src={tour.image_url || 'https://via.placeholder.com/400x300?text=No+Image'} alt={tour.title} />
                  </ImageWrapper>
                  <CardContent>
                    <CardTitle onClick={() => handleTourClick(tour)}>
                      <strong>{tour.title}</strong>
                    </CardTitle>
                    <CardDescription>{tour.description}</CardDescription>
                    <ButtonGroup>
                      <PrimaryButton onClick={() => handleTourClick(tour)}>Подробнее</PrimaryButton>
                      <SecondaryButton onClick={() => handleBookClick(tour)}>Забронировать</SecondaryButton>
                    </ButtonGroup>
                  </CardContent>
                </CardWrapper>
              </TourCard>
            ))}
          </ToursGrid>
          
          {tours.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>Туры скоро появятся...</div>
          )}
        </Content>
      </Container>

      <TourPopup 
        isOpen={isTourPopupOpen} 
        onClose={() => setIsTourPopupOpen(false)} 
        tourData={selectedTour}
        onBookClick={handleTourPopupBookClick} // Передаем функцию для открытия формы бронирования
      />
      <ChinaConsultPopup isOpen={isConsultPopupOpen} onClose={() => setIsConsultPopupOpen(false)} selectedTour={selectedTour} />
    </>
  );
};

export default ChinaToursPage;